import { graphCheckNames } from "@/lib/aicheck/graph-skeleton";
import { buildBlockedNodeIdSet } from "@/lib/aicheck/node-execution";
import type {
  ApiProgressInfo,
  FailedCheckInfo,
  RunProgress,
} from "@/types/aicheck";
import {
  type CheckInfo,
  NODE_IDS,
  type PipelineNodeData,
  type PipelineNodeStatus,
} from "./types";

// Per-run warning deduplication
const warnedKeys = new Set<string>();
let lastRunId: number | undefined;

function warnOnce(key: string, msg: string, ctx: Record<string, unknown>) {
  if (warnedKeys.has(key)) return;
  warnedKeys.add(key);
  console.warn(`[state-mapper] ${msg}`, ctx);
}

function resetWarningsIfNewRun(startedAt: number | undefined) {
  if (startedAt !== lastRunId) {
    warnedKeys.clear();
    lastRunId = startedAt;
  }
}

// Stage progression order — used to determine if a stage has been passed
const STAGE_ORDER = [
  "idle",
  "fetching-data",
  "compacting-contacts",
  "extracting-profile",
  "running-ai",
  "completed",
] as const;

function isPastStage(current: string, stage: string): boolean {
  const ci = STAGE_ORDER.indexOf(current as (typeof STAGE_ORDER)[number]);
  const si = STAGE_ORDER.indexOf(stage as (typeof STAGE_ORDER)[number]);
  return ci >= 0 && si >= 0 && ci > si;
}

export function mapProgressToNodeData(
  progress: RunProgress,
  existingNodeIds: Set<string>,
): Map<string, Partial<PipelineNodeData>> {
  resetWarningsIfNewRun(progress.startedAt);
  const updates = new Map<string, Partial<PipelineNodeData>>();
  const logContext = { runId: progress.startedAt };
  const observedNodeStates = progress.nodeStates ?? {};
  const scheduledBlockedNodeIds = buildBlockedNodeIdSet(
    progress.graphSkeleton,
    progress.nodeExecutionDirectives,
  );
  // **実行中は、ステージからの推測でノードを勝手に完了にしない。**
  //
  // このパイプラインは直線ではない。独立 AI チェックは compaction / profile と
  // **並行**に走るため、`status` は running-ai → compacting-contacts →
  // running-ai → extracting-profile … と行き来する（実測）。`isPastStage` は
  // 直線を前提にしているので、AI チェックが始まった瞬間に
  // 「extracting-profile より先へ進んだ」と読み、Customer Profile も
  // Profile Consistency もまだ動いていないのに緑になっていた。
  //
  // これらのノードはサーバが `node-state` で実際の状態を配る。実行中は
  // そちらだけを信じ、届いていないノードは「まだ」として扱う。
  // 実行中でない（保存済み結果を表示している）ときは、イベントが無いので
  // 従来どおり導出に頼る。
  const AUTHORITATIVE_NODE_IDS: string[] = [
    NODE_IDS.FETCH,
    NODE_IDS.CONSISTENCY_INDEPENDENT,
    NODE_IDS.COMPACTION,
    NODE_IDS.PROFILE,
    NODE_IDS.CONSISTENCY_PROFILE,
  ];
  const runIsLive =
    progress.status !== "idle" &&
    progress.status !== "completed" &&
    progress.status !== "error" &&
    progress.status !== "cancelled" &&
    progress.status !== "aborted";
  // **ノード状態が実際に届いている実行にだけ適用する。** 届いていない
  // （保存済み結果の表示、node-state を出さない古い実行、状態をまだ一度も
  // 受け取っていない瞬間）は、従来どおり導出に頼るしかない。
  const hasAuthoritativeNodeStates = Object.keys(observedNodeStates).length > 0;

  const overlayNodeStatus = (
    nodeId: string,
    status: PipelineNodeStatus,
  ): PipelineNodeStatus => {
    const observed = observedNodeStates[nodeId];
    if (
      runIsLive &&
      hasAuthoritativeNodeStates &&
      !observed &&
      status === "completed" &&
      AUTHORITATIVE_NODE_IDS.includes(nodeId)
    ) {
      return "pending";
    }
    const allowObservedQueuedState =
      progress.status !== "completed" &&
      progress.status !== "error" &&
      progress.status !== "cancelled";
    if (
      observed === "active" ||
      observed === "completed" ||
      observed === "failed" ||
      observed === "skipped" ||
      observed === "aborted" ||
      observed === "blocked"
    ) {
      return observed;
    }
    if (
      allowObservedQueuedState &&
      (observed === "planned" || observed === "pending") &&
      (status === "completed" || status === "planned" || status === "pending")
    ) {
      return observed;
    }
    if (
      scheduledBlockedNodeIds.has(nodeId) &&
      status !== "completed" &&
      status !== "failed" &&
      status !== "skipped" &&
      status !== "aborted"
    ) {
      return "blocked";
    }
    return status;
  };
  // Map reconnecting → running-ai for stage logic; disconnected/aborted → completed (terminal)
  const status =
    progress.status === "reconnecting"
      ? "running-ai"
      : progress.status === "disconnected" || progress.status === "aborted"
        ? "completed"
        : progress.status;
  const selectiveRerun = progress.selectiveRerun;

  const completedNames = new Set(progress.completedCheckNames ?? []);
  const activeMap = new Map<string, ApiProgressInfo>();
  for (const ac of progress.activeChecks ?? []) {
    activeMap.set(ac.checkName, ac);
  }
  const failedMap = new Map<string, FailedCheckInfo>();
  const failedBatchMap = new Map<string, FailedCheckInfo>();
  for (const fc of progress.failedChecks ?? []) {
    if (fc.batchGroupId) {
      failedBatchMap.set(fc.batchGroupId, fc);
    } else {
      failedMap.set(fc.checkName, fc);
    }
  }
  const checkIdByName = new Map<string, string>();
  for (const [checkId, checkName] of Object.entries(
    progress.plannedCheckNameById ?? {},
  )) {
    checkIdByName.set(checkName, checkId);
  }
  const skippedNames = new Set<string>();
  for (const si of progress.skippedItems ?? []) {
    skippedNames.add(si.name);
  }

  // --- System nodes ---

  // Fetch
  const fetchStatus: PipelineNodeStatus =
    progress.status === "idle"
      ? "planned"
      : selectiveRerun?.preserveFetch
        ? "completed"
        : progress.status === "fetching-data"
          ? "active"
          : "completed";
  updates.set(NODE_IDS.FETCH, {
    status: overlayNodeStatus(NODE_IDS.FETCH, fetchStatus),
  });

  // Compaction
  const compactionActive = activeMap.get("Contact Compaction");
  const compactionCompleted = completedNames.has("Contact Compaction");
  const compactionFailed = progress.failedStage === "compaction";
  const compactionStatus: PipelineNodeStatus = compactionFailed
    ? "failed"
    : compactionCompleted
      ? "completed"
      : compactionActive
        ? "active"
        : selectiveRerun?.preserveCompaction
          ? "completed"
          : status === "compacting-contacts"
            ? "active"
            : isPastStage(status, "compacting-contacts")
              ? "completed"
              : fetchStatus === "completed"
                ? "pending"
                : "planned";
  updates.set(NODE_IDS.COMPACTION, {
    status: overlayNodeStatus(NODE_IDS.COMPACTION, compactionStatus),
    ...(compactionFailed && progress.stageError
      ? { error: progress.stageError }
      : {}),
    ...(compactionActive
      ? {
          tokensReceived: compactionActive.tokensReceived,
          bytesReceived: compactionActive.bytesReceived,
          contentLength: compactionActive.contentLength,
        }
      : {}),
  });

  // Profile
  const profileActive = activeMap.get("Customer Profile");
  const profileCompleted = completedNames.has("Customer Profile");
  const profileFailed = progress.failedStage === "profile";
  const profileStatus: PipelineNodeStatus = profileFailed
    ? "failed"
    : profileCompleted
      ? "completed"
      : profileActive
        ? "active"
        : selectiveRerun?.preserveProfile
          ? "completed"
          : status === "extracting-profile"
            ? "active"
            : isPastStage(status, "extracting-profile")
              ? "completed"
              : compactionStatus === "completed" && !compactionFailed
                ? "pending"
                : "planned";
  updates.set(NODE_IDS.PROFILE, {
    status: overlayNodeStatus(NODE_IDS.PROFILE, profileStatus),
    ...(profileFailed && progress.stageError
      ? { error: progress.stageError }
      : {}),
    ...(profileActive
      ? {
          tokensReceived: profileActive.tokensReceived,
          bytesReceived: profileActive.bytesReceived,
          contentLength: profileActive.contentLength,
        }
      : {}),
  });

  // --- Skip cascades from failed stages ---
  const skipLaneB = compactionFailed;
  const skipDependent = compactionFailed || profileFailed;
  const hasAIError = progress.failedStage === "ai-checks";
  const skipAfterAll = hasAIError;

  if (skipLaneB) {
    updates.set(NODE_IDS.PROFILE, {
      status: overlayNodeStatus(NODE_IDS.PROFILE, "skipped"),
    });
  }

  // --- AI check nodes ---
  const laneMap = progress.checkLaneMap ?? {};

  // Build batch membership map: checkName → batchGroupId
  const checkToBatch = new Map<string, string>();
  const activeBatchMap = new Map<string, ApiProgressInfo>();
  for (const bg of progress.checkBatchGroups ?? []) {
    for (const name of bg.checks) {
      checkToBatch.set(name, bg.id);
    }
  }
  for (const ac of progress.activeChecks ?? []) {
    if (ac.batchGroupId) {
      activeBatchMap.set(ac.batchGroupId, ac);
    }
  }

  // Accumulate check infos per node (batch nodes collect multiple)
  const nodeCheckInfos = new Map<
    string,
    { checks: CheckInfo[]; lane: string | undefined }
  >();

  // **スケルトンを作る側と同じ規則で絞る。** `plannedChecks` には進捗表示用の
  // バッチ名（`"<グループ名> (batch 3)"`）も混ざるが、それはノードではない。
  const plannedCheckNames = graphCheckNames(
    progress.plannedChecks,
    progress.checkBatchGroups,
  );
  for (const name of plannedCheckNames) {
    const lane = laneMap[name];
    // Determine the target node ID
    const batchId = checkToBatch.get(name);
    let nodeId: string;
    if (lane === "afterAll") {
      nodeId = `summary-${name}`;
    } else if (batchId) {
      nodeId = `ai-batch-${batchId}`;
    } else {
      nodeId = `ai-${name}`;
    }

    if (!existingNodeIds.has(nodeId)) {
      warnOnce(`unmapped:${name}`, "Unmapped check", {
        ...logContext,
        nodeId,
        name,
      });
      continue;
    }

    const skipAsPending = skipAfterAll && lane === "afterAll";
    const isSkipped = skippedNames.has(name) && !skipAsPending;
    const batchFailed = batchId ? failedBatchMap.get(batchId) : undefined;
    const singleFailed = failedMap.get(name);
    const failedInfo = batchFailed ?? singleFailed;
    const isFailed = !!failedInfo;
    const isCompleted = completedNames.has(name);
    const activeInfo =
      activeMap.get(name) ??
      (batchId ? activeBatchMap.get(batchId) : undefined);
    const isActive = !!activeInfo;

    let checkStatus: PipelineNodeStatus;
    if (isSkipped) checkStatus = "skipped";
    else if (isFailed) checkStatus = "failed";
    else if (isCompleted) checkStatus = "completed";
    else if (isActive) checkStatus = "active";
    else if (skipDependent && lane === "dependent") checkStatus = "skipped";
    else if (skipAfterAll && lane === "afterAll") checkStatus = "planned";
    else checkStatus = "pending";

    const checkInfo: CheckInfo = {
      checkId: checkIdByName.get(name),
      name,
      status: checkStatus,
      ...(activeInfo
        ? {
            tokensReceived: activeInfo.tokensReceived,
            bytesReceived: activeInfo.bytesReceived,
            contentLength: activeInfo.contentLength,
            attempt: activeInfo.attempt,
          }
        : {}),
      ...(isFailed ? { error: failedInfo?.error } : {}),
    };

    const existing = nodeCheckInfos.get(nodeId);
    if (existing) {
      existing.checks.push(checkInfo);
    } else {
      nodeCheckInfos.set(nodeId, { checks: [checkInfo], lane });
    }
  }

  // Now emit updates for each node
  for (const [nodeId, { checks }] of nodeCheckInfos) {
    // Aggregate status: failed > active > completed > pending > skipped > planned
    let aggregated: PipelineNodeStatus = "pending";
    const hasActive = checks.some((c) => c.status === "active");
    const hasFailed = checks.some((c) => c.status === "failed");
    const allCompleted = checks.every((c) => c.status === "completed");
    const allSkipped = checks.every((c) => c.status === "skipped");

    if (hasFailed) aggregated = "failed";
    else if (hasActive) aggregated = "active";
    else if (allCompleted) aggregated = "completed";
    else if (allSkipped) aggregated = "skipped";
    else aggregated = checks[0]?.status ?? "pending";

    // Find first active check for streaming data
    const activeCheck = checks.find((c) => c.status === "active");

    updates.set(nodeId, {
      status: overlayNodeStatus(nodeId, aggregated),
      checks,
      ...(activeCheck
        ? {
            tokensReceived: activeCheck.tokensReceived,
            bytesReceived: activeCheck.bytesReceived,
            contentLength: activeCheck.contentLength,
          }
        : {}),
    });
  }

  // --- Consistency nodes ---
  // Independent consistency (pre-fork)
  const independentRules = progress.consistencyRuleResults ?? [];
  const iRuleCount = independentRules.length;
  const iPassedCount = independentRules.filter((r) => r.passed).length;
  const iFailedCount = iRuleCount - iPassedCount;
  updates.set(NODE_IDS.CONSISTENCY_INDEPENDENT, {
    status: overlayNodeStatus(
      NODE_IDS.CONSISTENCY_INDEPENDENT,
      selectiveRerun?.preserveIndependentConsistency
        ? "completed"
        : fetchStatus === "completed"
          ? "completed"
          : fetchStatus,
    ),
    ruleCount: iRuleCount,
    passedCount: iPassedCount,
    failedCount: iFailedCount,
    warningCount: 0,
    rules: independentRules,
  });

  // Profile-dependent consistency (lane-b, after profile)
  const profileRules = progress.profileConsistencyRuleResults ?? [];
  const pRuleCount = profileRules.length;
  const pPassedCount = profileRules.filter((r) => r.passed).length;
  const pFailedCount = pRuleCount - pPassedCount;
  const profileConsistencyStatus: PipelineNodeStatus = skipLaneB
    ? "skipped"
    : skipDependent
      ? "skipped"
      : selectiveRerun?.preserveProfileConsistency
        ? "completed"
        : pRuleCount > 0
          ? "completed"
          : isPastStage(status, "extracting-profile")
            ? "completed"
            : profileStatus === "completed"
              ? "pending"
              : "planned";
  updates.set(NODE_IDS.CONSISTENCY_PROFILE, {
    status: overlayNodeStatus(
      NODE_IDS.CONSISTENCY_PROFILE,
      profileConsistencyStatus,
    ),
    ruleCount: pRuleCount,
    passedCount: pPassedCount,
    failedCount: pFailedCount,
    warningCount: 0,
    rules: profileRules,
  });

  return updates;
}
